import { useEffect, useState } from "react";
import api, { errMsg } from "@/lib/api";
import { PageHeader } from "@/components/app/PageBits";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Search, FileText, Pencil, Trash2, Archive, ArchiveRestore, Merge } from "lucide-react";
import { fmtINR } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const EMPTY = { project_id: "", name: "", service_category: "", contact_name: "", phone: "", email: "", gstin: "", bank_details: "", upi_handle: "", address: "", notes: "" };
const EDIT_KEYS = ["name", "service_category", "contact_name", "phone", "email", "gstin", "bank_details", "upi_handle", "address", "notes"];

function VendorFields({ form, setForm, projects, prefix, showProject }) {
  const field = (key, label, extra = {}) => (
    <div className={extra.wide ? "col-span-2" : ""}>
      <Label>{label}</Label>
      <Input data-testid={`${prefix}-${extra.tid || key}`} value={form[key] || ""} placeholder={extra.placeholder}
        onChange={(e)=>setForm({ ...form, [key]: extra.upper ? e.target.value.toUpperCase() : e.target.value })}/>
    </div>
  );
  return (
    <div className="grid grid-cols-2 gap-3 py-2">
      {showProject && (
        <div className="col-span-2">
          <Label>Project</Label>
          <Select value={form.project_id} onValueChange={(v)=>setForm({...form,project_id:v})}>
            <SelectTrigger data-testid={`${prefix}-project`}><SelectValue placeholder="Select project"/></SelectTrigger>
            <SelectContent>{projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
          </Select>
        </div>
      )}
      {field("name", "Vendor name")}
      {field("service_category", "Service / category", { placeholder: "e.g. JCB Rental", tid: "service" })}
      {field("contact_name", "Contact person", { tid: "contact" })}
      {field("phone", "Phone")}
      {field("email", "Email")}
      {field("gstin", "GSTIN", { upper: true })}
      {field("upi_handle", "UPI handle", { tid: "upi" })}
      {field("bank_details", "Bank details", { wide: true, tid: "bank" })}
      {field("address", "Address", { wide: true })}
    </div>
  );
}

export default function Vendors() {
  const { user } = useAuth();
  const isSuper = user?.role === "super_admin";
  const [vendors, setVendors] = useState([]);
  const [projects, setProjects] = useState([]);
  const [q, setQ] = useState("");
  const [projectFilter, setProjectFilter] = useState("all");
  const [showArchived, setShowArchived] = useState(false);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editFor, setEditFor] = useState(null);
  const [editForm, setEditForm] = useState(EMPTY);
  const [delFor, setDelFor] = useState(null);
  const [delBlocked, setDelBlocked] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mergeFor, setMergeFor] = useState(null);
  const [mergeInto, setMergeInto] = useState("");
  const [mergeOptions, setMergeOptions] = useState([]);

  const openMerge = async (v) => {
    setMergeFor(v); setMergeInto("");
    const r = await api.get("/vendors", { params: { project_id: v.project_id } });
    setMergeOptions(r.data.filter((x) => x.id !== v.id));
  };
  const confirmMerge = async () => {
    const target = mergeOptions.find((x) => x.id === mergeInto);
    setBusy(true);
    try {
      const r = await api.post(`/vendors/${mergeFor.id}/merge`, { into_id: mergeInto });
      toast.success(`${mergeFor.name} merged into ${target.name}`, { description: `${r.data.expenses_moved} bill(s) and ${r.data.payments_moved} payment(s) moved` });
      setMergeFor(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };
  const [stmtOpen, setStmtOpen] = useState(false);
  const [stmtFor, setStmtFor] = useState(null);
  const [stmtRange, setStmtRange] = useState({ d_from: "", d_to: "" });

  const load = async () => {
    const params = {};
    if (q.trim()) params.q = q.trim();
    if (projectFilter !== "all") params.project_id = projectFilter;
    if (showArchived) params.archived = true;
    const [v, p] = await Promise.all([api.get("/vendors", { params }), api.get("/projects")]);
    setVendors(v.data); setProjects(p.data);
  };
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, projectFilter, showArchived]);

  const submit = async () => {
    if (!form.project_id) { toast.error("Select a project"); return; }
    if (!form.name.trim()) { toast.error("Enter the vendor name"); return; }
    try { await api.post("/vendors", form); toast.success("Vendor added"); setOpen(false); setForm(EMPTY); load(); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const openEdit = (v) => { setEditFor(v); setEditForm(Object.fromEntries(EDIT_KEYS.map((k) => [k, v[k] || ""]))); };
  const saveEdit = async () => {
    if (!editForm.name.trim()) { toast.error("Enter the vendor name"); return; }
    setBusy(true);
    try { await api.patch(`/vendors/${editFor.id}`, editForm); toast.success("Vendor updated"); setEditFor(null); load(); }
    catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const setArchived = async (v, archived) => {
    setBusy(true);
    try {
      await api.patch(`/vendors/${v.id}`, { archived });
      toast.success(archived ? `${v.name} archived` : `${v.name} restored`);
      setDelFor(null); load();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(false); }
  };

  const confirmDelete = async () => {
    setBusy(true);
    try { await api.delete(`/vendors/${delFor.id}`); toast.success(`${delFor.name} deleted`); setDelFor(null); load(); }
    catch (e) {
      if (e?.response?.status === 409) setDelBlocked(errMsg(e));
      else toast.error(errMsg(e));
    } finally { setBusy(false); }
  };

  const openStmt = (v) => {
    const today = new Date();
    const first = new Date(today.getFullYear(), today.getMonth(), 1);
    setStmtFor(v);
    setStmtRange({ d_from: first.toISOString().slice(0,10), d_to: today.toISOString().slice(0,10) });
    setStmtOpen(true);
  };

  const downloadStmt = async () => {
    try {
      const r = await api.get(`/vendors/${stmtFor.id}/statement.pdf`, {
        params: { d_from: stmtRange.d_from, d_to: stmtRange.d_to },
        responseType: "blob",
      });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `vendor_statement_${stmtFor.name.replace(/\s+/g,"_")}_${stmtRange.d_from}_${stmtRange.d_to}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      setStmtOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Statement failed"); }
  };

  return (
    <div>
      <PageHeader
        title="Vendors"
        subtitle="Service providers and suppliers per project. Track bills, payments, and generate statements."
        actions={
          <div className="flex gap-2 items-center">
            <div className="relative">
              <Search className="h-4 w-4 absolute left-2.5 top-2.5 text-muted-foreground pointer-events-none"/>
              <Input data-testid="vendor-search" placeholder="Search name / phone / email / service"
                value={q} onChange={(e)=>setQ(e.target.value)} className="pl-8 w-72"/>
            </div>
            <Select value={projectFilter} onValueChange={setProjectFilter}>
              <SelectTrigger data-testid="vendor-project-filter" className="w-48"><SelectValue/></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All projects</SelectItem>
                {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {isSuper && (
              <label className="flex items-center gap-1.5 text-xs text-muted-foreground px-2 cursor-pointer">
                <input data-testid="vendor-show-archived" type="checkbox" checked={showArchived} onChange={(e)=>setShowArchived(e.target.checked)}/>
                Show archived
              </label>
            )}
            <Dialog open={open} onOpenChange={setOpen}>
              <DialogTrigger asChild><Button data-testid="new-vendor-btn" className="fab-m"><Plus className="h-4 w-4 mr-2"/>New vendor</Button></DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader><DialogTitle>New vendor</DialogTitle></DialogHeader>
                <VendorFields form={form} setForm={setForm} projects={projects} prefix="vendor" showProject />
                <DialogFooter><Button data-testid="vendor-save-btn" onClick={submit}>Create vendor</Button></DialogFooter>
              </DialogContent>
            </Dialog>
          </div>
        }
      />

      <div className="border border-border rounded-md bg-card overflow-hidden">
        <table className="w-full text-sm m-cards">
          <thead className="bg-muted/60"><tr className="text-left">
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Name</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Project</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Service</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider">Contact</th>
            <th className="px-4 py-3 font-heading text-xs uppercase tracking-wider text-right">Outstanding</th>
            <th className="px-4 py-3"></th>
          </tr></thead>
          <tbody>
            {vendors.map(v => (
              <tr key={v.id} data-testid={`vendor-row-${v.id}`} className={`border-t border-border hover:bg-muted/40 ${v.archived ? "bg-stone-50 text-stone-500" : ""}`}>
                <td className="px-4 py-3 font-medium">
                  {v.name}
                  {v.archived && <span data-testid={`vendor-archived-badge-${v.id}`} className="ml-2 text-[10px] uppercase tracking-widest rounded border border-border px-1.5 py-0.5 text-muted-foreground">Archived</span>}
                </td>
                <td className="px-4 py-3 text-muted-foreground">{v.project_name}</td>
                <td className="px-4 py-3 text-muted-foreground">{v.service_category || "-"}</td>
                <td className="px-4 py-3 text-muted-foreground">{v.contact_name || v.phone || "-"}</td>
                <td className="px-4 py-3 text-right tabular text-destructive">{fmtINR(v.balance)}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex gap-1 justify-end">
                    <Button data-testid={`stmt-${v.id}`} size="sm" variant="outline" onClick={()=>openStmt(v)}>
                      <FileText className="h-3.5 w-3.5 mr-1"/>Statement
                    </Button>
                    {isSuper && !v.archived && <>
                      <Button data-testid={`vendor-edit-${v.id}`} size="sm" variant="ghost" title="Edit" onClick={()=>openEdit(v)}><Pencil className="h-3.5 w-3.5"/></Button>
                      <Button data-testid={`vendor-merge-${v.id}`} size="sm" variant="ghost" title="Merge into another vendor" onClick={()=>openMerge(v)}><Merge className="h-3.5 w-3.5"/></Button>
                      <Button data-testid={`vendor-delete-${v.id}`} size="sm" variant="ghost" title="Delete" className="text-destructive hover:text-destructive" onClick={()=>{ setDelBlocked(null); setDelFor(v); }}><Trash2 className="h-3.5 w-3.5"/></Button>
                    </>}
                    {isSuper && v.archived && (
                      <Button data-testid={`vendor-restore-${v.id}`} size="sm" variant="ghost" disabled={busy} onClick={()=>setArchived(v, false)}><ArchiveRestore className="h-3.5 w-3.5 mr-1"/>Restore</Button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {vendors.length === 0 && <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">No vendors yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={!!editFor} onOpenChange={(v)=>{ if (!v) setEditFor(null); }}>
        <DialogContent className="max-w-2xl" data-testid="vendor-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit vendor</DialogTitle>
            <DialogDescription>{editFor?.project_name}. Name changes also update past expenses.</DialogDescription>
          </DialogHeader>
          <VendorFields form={editForm} setForm={setEditForm} projects={projects} prefix="vedit" />
          <DialogFooter>
            <Button data-testid="vedit-save-btn" onClick={saveEdit} disabled={busy}>{busy ? "Saving…" : "Save changes"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!delFor} onOpenChange={(v)=>{ if (!v) setDelFor(null); }}>
        <DialogContent className="max-w-md" data-testid="vendor-delete-dialog">
          <DialogHeader>
            <DialogTitle>{delBlocked ? "Can't delete this vendor" : `Delete ${delFor?.name}?`}</DialogTitle>
            <DialogDescription data-testid="vendor-delete-message">
              {delBlocked || "This permanently removes the vendor. It can't be undone."}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button data-testid="vendor-delete-cancel" variant="outline" onClick={()=>setDelFor(null)} disabled={busy}>Cancel</Button>
            {delBlocked
              ? <Button data-testid="vendor-archive-btn" onClick={()=>setArchived(delFor, true)} disabled={busy}><Archive className="h-4 w-4 mr-2"/>Archive instead</Button>
              : <Button data-testid="vendor-delete-confirm" variant="destructive" onClick={confirmDelete} disabled={busy}><Trash2 className="h-4 w-4 mr-2"/>Delete</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!mergeFor} onOpenChange={(v)=>{ if (!v) setMergeFor(null); }}>
        <DialogContent className="max-w-md" data-testid="vendor-merge-dialog">
          <DialogHeader>
            <DialogTitle>Merge {mergeFor?.name}</DialogTitle>
            <DialogDescription>All bills, payments and the outstanding {fmtINR(mergeFor?.balance || 0)} move to the vendor you keep. Its empty details are filled from {mergeFor?.name}, then {mergeFor?.name} is deleted.</DialogDescription>
          </DialogHeader>
          <div>
            <Label>Keep this vendor ({mergeFor?.project_name})</Label>
            <Select value={mergeInto} onValueChange={setMergeInto}>
              <SelectTrigger data-testid="vendor-merge-target"><SelectValue placeholder={mergeOptions.length ? "Select vendor to keep" : "No other vendors in this project"}/></SelectTrigger>
              <SelectContent>{mergeOptions.map((x) => <SelectItem key={x.id} value={x.id}>{x.name}{x.phone ? ` · ${x.phone}` : ""}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <DialogFooter>
            <Button data-testid="vendor-merge-cancel" variant="outline" onClick={()=>setMergeFor(null)} disabled={busy}>Cancel</Button>
            <Button data-testid="vendor-merge-confirm" onClick={confirmMerge} disabled={busy || !mergeInto}><Merge className="h-4 w-4 mr-2"/>{busy ? "Merging…" : "Merge"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stmtOpen} onOpenChange={setStmtOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Vendor statement · {stmtFor?.name}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div><Label>From</Label><Input type="date" data-testid="stmt-from" value={stmtRange.d_from} onChange={(e)=>setStmtRange({...stmtRange, d_from:e.target.value})}/></div>
            <div><Label>To</Label><Input type="date" data-testid="stmt-to" value={stmtRange.d_to} onChange={(e)=>setStmtRange({...stmtRange, d_to:e.target.value})}/></div>
          </div>
          <DialogFooter><Button data-testid="stmt-download" onClick={downloadStmt}>Download PDF</Button></DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
